import { pickBy, range, values } from 'lodash';
import { DateTime } from 'luxon';
import * as z from 'zod';

import { convertStringToDate, CreateLoanRow } from '@willow/types-iso';

import { getStartOfFirstCollectedBillingPeriod } from '../../../utils';
import { LoanWarn, loanWarningRule } from '../../shared/apiWarnings';
import { NUM_BOARDING_INSURANCES } from './createFieldConsts';
import {
  BASE_ESCROW_CITY_FIELDS_FOR_VALIDATION,
  BASE_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
  BASE_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
  BASE_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
  BASE_INSURANCE_FIELDS_FOR_VALIDATION,
  CrossFieldParserContext,
  getParsedFields,
  MULTI_PROP_ESCROW_CITY_FIELDS_FOR_VALIDATION,
  MULTI_PROP_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
  MULTI_PROP_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
  MULTI_PROP_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
  MULTI_PROP_INSURANCE_FIELDS_FOR_VALIDATION,
} from './CreateLoanCrossFieldParser';
import {
  validateAppraisalFields,
  validateAppraisalHasRequiredFieldsOrNone,
} from './crossFieldParsers/validateAppraisalFields';
import { validateAuthorizedContactFields } from './crossFieldParsers/validateAuthorizedContactFields';
import { validateBorrowerFields } from './crossFieldParsers/validateBorrowerFields';
import { validateFeeFields } from './crossFieldParsers/validateFeeFields';

const firstCollectedDateIsInPast = (data: CreateLoanRow, warn: LoanWarn) => {
  const { firstCollectedPaymentDate } = data;

  if (!firstCollectedPaymentDate) {
    return;
  }

  if (convertStringToDate(firstCollectedPaymentDate) < new Date()) {
    warn('FIRST_COLLECTED_PAYMENT_DATE_IN_PAST');
  }
};

const stopEmailOrPayments = (data: CreateLoanRow, warn: LoanWarn) => {
  const { stopEmails, stopPayments } = data;

  if (stopEmails) {
    warn('STOP_EMAILS_FLAGS_LOAN');
  }

  if (stopPayments) {
    warn('STOP_PAYMENTS_FLAGS_LOAN');
  }
};

const agedLoanHasBeginningOfYearPrincipalBalance = (data: CreateLoanRow, warn: LoanWarn) => {
  const { acquisitionDate, fundingDate, firstCollectedPaymentDate, beginningOfYearPrincipalBalance, ytdInterestPaid } =
    data;

  if (firstCollectedPaymentDate) {
    const lenderOwnershipStartDate = acquisitionDate || fundingDate;
    const lenderTookOwnershipInYearPriorToWillowServicing =
      DateTime.fromJSDate(convertStringToDate(lenderOwnershipStartDate)).year <
      DateTime.fromJSDate(convertStringToDate(firstCollectedPaymentDate)).year;

    // Lender has been servicing this loan since a previous year, so we need to know the starting year principal balance for tax reporting
    if (lenderTookOwnershipInYearPriorToWillowServicing && !beginningOfYearPrincipalBalance) {
      warn('MISSING_BEGINNING_OF_YEAR_PRINCIPAL_BALANCE');
    }

    if (lenderTookOwnershipInYearPriorToWillowServicing && !ytdInterestPaid) {
      warn('MISSING_YTD_INTEREST_PAID');
    }
  }
};

const escrowInsuranceHasPolicyDates = (data: CreateLoanRow, warn: LoanWarn) => {
  for (const n of range(1, NUM_BOARDING_INSURANCES + 1)) {
    if (
      // @ts-ignore
      data[`insurance${n}IsEscrowed`] === true ||
      // @ts-ignore
      (data[`insurance${n}Amount`] != null && data[`insurance${n}Amount`] > 0) ||
      // @ts-ignore
      data[`insurance${n}CompanyName`] != null ||
      // @ts-ignore
      data[`insurance${n}PolicyNumber`] != null
    ) {
      const missingFields: string[] = [];

      for (const key of [`insurance${n}PolicyEnd`, `insurance${n}PolicyNumber`]) {
        // @ts-ignore
        if (data[key] == null) {
          missingFields.push(key);
        }
      }

      if (missingFields.length) {
        const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
        warn('INSURANCE_POLICY_INCOMPLETE', {
          message: `Insurance policy ${n} is missing ${
            missingFields.length > 1 ? 'fields' : 'a field'
          } and will not be added to the loan: ${missingFieldsString}`,
        });
      }
    }
  }
};

const escrowCompaniesHavePolicyNumber = (data: CreateLoanRow, warn: LoanWarn) => {
  for (const n of range(1, 4)) {
    // @ts-ignore
    if (data[`escrow${n}CompanyName`] != null && data[`escrow${n}PolicyNumber`] == null) {
      warn('ESCROW_COMPANY_MISSING_POLICY_NUMBER', {
        message: `Escrow company ${n} is missing \`escrow${n}PolicyNumber\` and will not be added to the loan`,
      });
    }
  }
};

const validateAcquisitionFields = (data: CreateLoanRow, warn: LoanWarn) => {
  const { correspondentLender, correspondentLenderPhone, correspondentLenderEmail } = data;
  if (correspondentLenderPhone && !correspondentLender) {
    warn('CORRESPONDENT_LENDER_PHONE_WITHOUT_LENDER');
  }
  if (correspondentLenderEmail && !correspondentLender) {
    warn('CORRESPONDENT_LENDER_EMAIL_WITHOUT_LENDER');
  }
};

const hasMonthlyMortgageInsuranceField =
  (context: CrossFieldParserContext) => (data: CreateLoanRow, warn: LoanWarn) => {
    const parsedEscrowLines =
      context === 'api'
        ? getParsedFields(data, [
            ...MULTI_PROP_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_ESCROW_CITY_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
            ...MULTI_PROP_INSURANCE_FIELDS_FOR_VALIDATION,
          ])
        : getParsedFields(data, [
            ...BASE_ESCROW_COUNTY_FIELDS_FOR_VALIDATION,
            ...BASE_ESCROW_CITY_FIELDS_FOR_VALIDATION,
            ...BASE_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION,
            ...BASE_ESCROW_OTHER_FIELDS_FOR_VALIDATION,
            ...BASE_INSURANCE_FIELDS_FOR_VALIDATION,
          ]);

    const parsedFields = getParsedFields(data, ['monthlyPaymentMi', 'isEscrowed', 'monthlyPaymentEscrow', 'miAmount']);

    if (!parsedEscrowLines || !parsedFields) {
      return;
    }

    const isEscrowedFields = pickBy(parsedEscrowLines, (_, key) => key.toLowerCase().includes('isescrowed'));

    const hasEscrowLines = values(isEscrowedFields).some((v) => v != null);
    const isEscrowed =
      values(isEscrowedFields).some((v) => v === true) ||
      parsedFields.isEscrowed === true ||
      !!parsedFields.monthlyPaymentEscrow;

    if (hasEscrowLines && isEscrowed && parsedFields.monthlyPaymentMi == null && parsedFields.miAmount == null) {
      warn('ESCROWED_LOAN_MISSING_MORTGAGE_INSURANCE');
    }
  };

const hasInitialOutstandingInterestOnEscrow =
  (options: { interestOnEscrowEnabled: boolean }) => (data: CreateLoanRow, warn: LoanWarn) => {
    if (!options.interestOnEscrowEnabled) {
      return;
    }

    const parsed = getParsedFields(data, [
      'fundingDate',
      'firstPaymentDate',
      'firstCollectedPaymentDate',
      'paymentFrequency',
      'initialOutstandingInterestOnEscrow',
      'omitFromInterestOnEscrow',
    ]);

    if (!parsed || !parsed.fundingDate || !parsed.firstPaymentDate || parsed.omitFromInterestOnEscrow) {
      return;
    }

    const startOfFirstCollectedBillingPeriod = getStartOfFirstCollectedBillingPeriod(
      convertStringToDate(parsed.firstCollectedPaymentDate || parsed.firstPaymentDate),
      parsed.paymentFrequency ?? 12,
    );
    const fundingDate = DateTime.fromJSDate(convertStringToDate(parsed.fundingDate)).startOf('day').toJSDate();

    if (fundingDate < startOfFirstCollectedBillingPeriod && parsed.initialOutstandingInterestOnEscrow == null) {
      warn('MISSING_INITIAL_OUTSTANDING_INTEREST_ON_ESCROW');
    }
  };

// Mirrors the blocking findings in createGinnieMaeRFSMonthlyReport (WILLOW-RFS-LOANID01
// and WILLOW-RFS-LT01) so data problems surface at board time instead of at the GNMA
// reporting deadline. A missing investorLoanId and GNMA-like ownerId values are already
// hard errors in validateInvestorFields, so this only covers what passes those checks:
// a present but malformed Unique Loan ID, and a product type with no GNMA mapping.
const GNMA_ELIGIBLE_PRODUCT_TYPES: ReadonlySet<string> = new Set(['fha', 'va', 'usda', 'pih']);

const ginnieMaeReportingFields = (data: CreateLoanRow, warn: LoanWarn) => {
  const { investorName, investorLoanId, productType } = data;
  if (investorName !== 'GNMA') {
    return;
  }

  if (investorLoanId && !/^\d{1,9}$/.test(investorLoanId)) {
    warn('GNMA_INVESTOR_LOAN_ID_NOT_NUMERIC');
  }

  if (!productType) {
    warn('GNMA_PRODUCT_TYPE_MISSING');
  } else if (!GNMA_ELIGIBLE_PRODUCT_TYPES.has(productType)) {
    warn('GNMA_PRODUCT_TYPE_NOT_ELIGIBLE', {
      message: `Product type \`${productType}\` does not map to a Ginnie Mae loan type (fha, va, usda, or pih), so the loan cannot be included in GNMA RFS reporting.`,
    });
  }
};

const interestOnlyFields = (data: CreateLoanRow, warn: LoanWarn) => {
  const { interestOnlyFlag, interestOnlyTermMonths, interestOnlyTermYears, interestOnlyEndDate, interestType } = data;
  if (!interestOnlyFlag && interestType) {
    warn('INTEREST_TYPE_WITHOUT_INTEREST_ONLY');
  }

  if (!interestOnlyFlag) {
    for (const [field, value] of [
      ['interestOnlyTermMonths', interestOnlyTermMonths],
      ['interestOnlyTermYears', interestOnlyTermYears],
      ['interestOnlyEndDate', interestOnlyEndDate],
    ] as const) {
      if (value != null) {
        warn('INTEREST_ONLY_FLAG_REQUIRED', { path: [field] });
        break;
      }
    }
  }
};

export type CreateLoanWarningParserOptions = {
  interestOnEscrowEnabled?: boolean;
};

export const getCreateLoanWarningParser = (
  context: CrossFieldParserContext,
  options?: CreateLoanWarningParserOptions,
) =>
  z
    .any()
    // These rules take a LoanWarn rather than a RefinementCtx, so each one can only raise a
    // warning that exists in LOAN_API_WARNINGS — and therefore in the published docs.
    .superRefine(loanWarningRule(firstCollectedDateIsInPast))
    .superRefine(loanWarningRule(stopEmailOrPayments))
    .superRefine(loanWarningRule(agedLoanHasBeginningOfYearPrincipalBalance))
    .superRefine(loanWarningRule(escrowInsuranceHasPolicyDates))
    .superRefine(loanWarningRule(escrowCompaniesHavePolicyNumber))
    .superRefine(loanWarningRule(validateAcquisitionFields))
    .superRefine(loanWarningRule(hasMonthlyMortgageInsuranceField(context)))
    .superRefine(
      loanWarningRule(
        hasInitialOutstandingInterestOnEscrow({ interestOnEscrowEnabled: options?.interestOnEscrowEnabled ?? false }),
      ),
    )
    .superRefine(loanWarningRule(interestOnlyFields))
    .superRefine(loanWarningRule(ginnieMaeReportingFields))
    .superRefine(loanWarningRule(validateBorrowerFields))
    .superRefine(loanWarningRule(validateAuthorizedContactFields))
    .superRefine(loanWarningRule(validateAppraisalFields<LoanWarn>([validateAppraisalHasRequiredFieldsOrNone])))
    // The one rule still shared with the cross-field (error) parser, called there identically,
    // so its issues reach a caller as errors and never as warnings.
    .superRefine(validateFeeFields);
